'use client';

import { ThemeProvider as MaterialUIThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme } from '@/lib/mui-theme';
import { createContext, useContext, useState, useEffect } from 'react';

interface ThemeContextType {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  whiteLabelSettings: WhiteLabelSettings | null; // Add whiteLabelSettings to context
  loadingWhiteLabel: boolean;
}

interface WhiteLabelSettings {
  appName: string;
  appLogoUrl: string;
  primaryColor: string;
  secondaryColor: string;
  enableCustomDomain: boolean;
  customDomain: string;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function useAppTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    // If used on the server (or outside the provider), return a safe fallback to avoid runtime errors
    return {
      theme: 'light' as 'light' | 'dark',
      toggleTheme: () => {},
      whiteLabelSettings: null,
      loadingWhiteLabel: true,
    };
  }
  return context;
}

interface AppThemeProviderProps {
  children: React.ReactNode;
}

export function AppThemeProvider({ children }: AppThemeProviderProps) {
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [mounted, setMounted] = useState(false);
  const [whiteLabelSettings, setWhiteLabelSettings] = useState<WhiteLabelSettings | null>(null);
  const [loadingWhiteLabel, setLoadingWhiteLabel] = useState(true);
  const [errorWhiteLabel, setErrorWhiteLabel] = useState<string | null>(null);

  // Initialize theme from localStorage on mount and sync html class so body background matches
  useEffect(() => {
    setMounted(true);
    const savedTheme = localStorage.getItem('creatorflow-theme') as 'light' | 'dark' | null;
    const resolved = savedTheme || (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
    if (savedTheme) {
      setTheme(savedTheme);
    } else {
      setTheme(resolved);
    }
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(resolved);

    const fetchWhiteLabelSettings = async () => {
      try {
        const response = await fetch('/api/admin/white-label');
        if (!response.ok) {
          // 404 = route not implemented or not available; treat as no white-label (use defaults)
          if (response.status === 404) {
            setLoadingWhiteLabel(false);
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data: WhiteLabelSettings = await response.json();
        setWhiteLabelSettings(data);
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        setErrorWhiteLabel(`Failed to load white-label settings: ${errorMessage}`);
        if (process.env.NODE_ENV === 'development') {
          console.debug('White-label fetch failed (using defaults):', errorMessage);
        }
      } finally {
        setLoadingWhiteLabel(false);
      }
    };
    fetchWhiteLabelSettings();

  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('creatorflow-theme', newTheme);
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(newTheme);
  };

  // When white-label fetch fails, we already use defaults below (createAppTheme handles null).
  // Only log in development to avoid console noise; no throw.
  if (errorWhiteLabel && process.env.NODE_ENV === 'development') {
    console.debug(errorWhiteLabel);
  }

  const muiTheme = createAppTheme(theme, whiteLabelSettings);
  // Ensure CSS variable mirrors the resolved primary color so any inline styles
  // or CSS that reference --floai-primary use the sanitized color at runtime.
  useEffect(() => {
    try {
      const primary = (muiTheme && muiTheme.palette && muiTheme.palette.primary && muiTheme.palette.primary.main) || '#2563EB';
      document.documentElement.style.setProperty('--floai-primary', primary);
    } catch (e) {
      // ignore
    }
  }, [muiTheme]);

  // Inject a high-priority style tag at the end of <body> so it wins over MUI/Emotion
  // (which can inject after our effect). Use literal hex for contained primary so it never fails.
  useEffect(() => {
    try {
      const id = 'floai-primary-override';
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const style = document.createElement('style');
      style.id = id;
      const primaryMain = (muiTheme?.palette?.primary?.main) || '#2563EB';
      const primaryDark = (muiTheme?.palette?.primary?.dark) || '#1e40af';
      style.innerHTML = `
        :root { --floai-primary: ${primaryMain} !important; --floai-primary-dark: ${primaryDark} !important; }
        .MuiButton-outlinedPrimary,
        .MuiButton-textPrimary {
          color: var(--floai-primary-dark) !important;
          border-color: var(--floai-primary-dark) !important;
        }
        .MuiButton-containedPrimary,
        .MuiButton-contained.MuiButton-colorPrimary,
        .MuiButtonBase-root.MuiButton-root.MuiButton-contained.MuiButton-containedPrimary {
          background-color: ${primaryMain} !important;
          color: #ffffff !important;
        }
        .MuiButton-containedPrimary:hover:not(:disabled),
        .MuiButton-contained.MuiButton-colorPrimary:hover:not(:disabled) {
          background-color: ${primaryDark} !important;
          color: #ffffff !important;
        }
        .MuiChip-root.MuiChip-colorPrimary, .MuiChip-filledPrimary {
          background-color: var(--floai-primary-dark) !important;
          color: #ffffff !important;
        }
        .MuiSvgIcon-colorPrimary, .MuiSvgIcon-root.MuiSvgIcon-colorPrimary {
          color: var(--floai-primary) !important;
        }
      `;
      document.body.appendChild(style);
      return () => {
        const el = document.getElementById(id);
        if (el) el.remove();
      };
    } catch (e) {
      // ignore
    }
  }, [muiTheme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, whiteLabelSettings, loadingWhiteLabel }}>
      <MaterialUIThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MaterialUIThemeProvider>
    </ThemeContext.Provider>
  );
} 